import { GridFilterItem } from '@mui/x-data-grid-pro';

import { SHIPMENT_FILTER_FIELD_DEFINITIONS } from './shipmentFilterDefinitions';
import type {
  DropdownOption,
  FilterState,
  QuickFilterDef,
} from '../../../components/molecules/FilterBar';
import { emptyQuickState, quickList, quickText } from '../../../components/molecules/FilterBar';
import { advancedFiltersFromModel } from '../../../utils/advancedFilterFromModel';
import { advancedFiltersToItems } from '../../../utils/filterModel';

export interface LabelValue {
  label: string;
  value: string;
}

/** Quick-filter slot keys. Exported so the config and `buildFilterModel` can't
 *  drift apart on a string literal. */
export const SHIPMENT_QUICK = {
  shipmentTypes: 'shipmentTypeCode',
  shipmentId: 'id',
  connoteNo: 'connoteNo',
  statuses: 'shipmentStatusCode',
  destination: 'destinationName',
} as const;

/** Two dropdowns and three text inputs, no search box. Both option lists are
 *  fetched, so the config is built per render; memoise it at the call site. */
export const buildShipmentQuickFilters = ({
  statusOptions,
  typeOptions,
}: {
  statusOptions: DropdownOption[];
  typeOptions: DropdownOption[];
}): QuickFilterDef[] => [
  {
    kind: 'multiselect',
    key: SHIPMENT_QUICK.shipmentTypes,
    label: 'Shipment Type',
    options: typeOptions,
  },
  { kind: 'text', key: SHIPMENT_QUICK.shipmentId, label: 'Shipment ID', width: 120 },
  {
    kind: 'text',
    key: SHIPMENT_QUICK.connoteNo,
    label: 'Connote No.',
    width: 160,
    ariaLabel: 'Connote number filter',
  },
  {
    kind: 'multiselect',
    key: SHIPMENT_QUICK.statuses,
    label: 'Status',
    options: statusOptions,
  },
  { kind: 'text', key: SHIPMENT_QUICK.destination, label: 'Destination', width: 180 },
];

export const EMPTY_FILTERS: FilterState = {
  statuses: [],
  jobTypes: [],
  contracts: [],
  contractNames: [],
  extraText: '',
  jobId: '',
  multiSearch: '',
  advancedFilters: [],
  quick: emptyQuickState(buildShipmentQuickFilters({ statusOptions: [], typeOptions: [] })),
};

export const buildFilterModel = (filters: FilterState): { items: GridFilterItem[] } => {
  const items: GridFilterItem[] = [];

  // Same array-valued `isAnyOf` the status slot uses: this BFF reads the
  // repeated param as a list, unlike the comma-joined pages.
  const shipmentTypes = quickList(filters.quick, SHIPMENT_QUICK.shipmentTypes);
  if (shipmentTypes.length === 1) {
    items.push({
      field: SHIPMENT_QUICK.shipmentTypes,
      operator: 'equals',
      value: shipmentTypes[0],
    });
  } else if (shipmentTypes.length > 1) {
    items.push({ field: SHIPMENT_QUICK.shipmentTypes, operator: 'isAnyOf', value: shipmentTypes });
  }

  const shipmentId = quickText(filters.quick, SHIPMENT_QUICK.shipmentId).trim();
  if (shipmentId) {
    // The shipments BFF (api-logistics) passes this filter through
    // `getSequelizeOperator` from lib-framework, which maps `equals`/`isAnyOf`
    // to `Op.iLike` — that errors on the numeric `id` column and the route
    // returns `{}`. Only the literal `=` operator falls through to `Op.eq` and
    // matches a numeric id. (api-services has bespoke numeric-column handling
    // for `serviceId`, which is why the Services list pattern doesn't carry
    // over here.) This mirrors the legacy MUI DataGrid behaviour where the
    // `id` column was `type: 'number'` (default operator: `=`).
    items.push({ field: 'id', operator: '=', value: shipmentId });
  }

  const connoteNo = quickText(filters.quick, SHIPMENT_QUICK.connoteNo).trim();
  if (connoteNo) {
    items.push({ field: 'connoteNo', operator: 'contains', value: connoteNo });
  }

  const statuses = quickList(filters.quick, SHIPMENT_QUICK.statuses);
  if (statuses.length === 1) {
    items.push({ field: 'shipmentStatusCode', operator: 'equals', value: statuses[0] });
  } else if (statuses.length > 1) {
    // Array, not a comma-joined string: this page's wire shape predates the
    // shared expansion and the BFF reads the repeated param as a list.
    items.push({ field: 'shipmentStatusCode', operator: 'isAnyOf', value: statuses });
  }

  const destination = quickText(filters.quick, SHIPMENT_QUICK.destination).trim();
  if (destination) {
    items.push({ field: 'destinationName', operator: 'contains', value: destination });
  }

  // Shipments talks to a lib-framework BFF (api-logistics): collapses repeated
  // params, supports `isNotAnyOf` for multi-value `Is not`.
  items.push(...advancedFiltersToItems(filters.advancedFilters, 'isNotAnyOf'));

  return { items };
};

/** A dropdown slot leaves the wire as `equals` (a single value) or `isAnyOf`
 *  (several). Both of this page's dropdowns read back the same way. */
const listValuesOf = (item: GridFilterItem): string[] => {
  if (item.operator === 'isAnyOf' && Array.isArray(item.value)) return item.value.map(String);
  if (item.operator === 'equals' && item.value != null) return [String(item.value)];
  return [];
};

export const filterModelToFilterState = (model: { items: GridFilterItem[] }): FilterState => {
  const quick: Record<string, string | string[]> = {
    ...emptyQuickState(buildShipmentQuickFilters({ statusOptions: [], typeOptions: [] })),
  };
  const statuses: string[] = [];
  const shipmentTypes: string[] = [];

  // Predicates outside the quick-filter slots become Add Filter chips;
  // `advancedFiltersFromModel` owns that reconstruction.
  const rest: GridFilterItem[] = [];

  for (const item of model.items) {
    if (item.field === 'id' && item.value != null) {
      quick[SHIPMENT_QUICK.shipmentId] = String(item.value);
    } else if (item.field === 'connoteNo' && item.operator === 'contains' && item.value != null) {
      quick[SHIPMENT_QUICK.connoteNo] = String(item.value);
    } else if (item.field === 'shipmentTypeCode') {
      shipmentTypes.push(...listValuesOf(item));
    } else if (item.field === 'shipmentStatusCode') {
      statuses.push(...listValuesOf(item));
    } else if (
      item.field === 'destinationName' &&
      item.operator === 'contains' &&
      item.value != null
    ) {
      quick[SHIPMENT_QUICK.destination] = String(item.value);
    } else {
      rest.push(item);
    }
  }

  // Malformed inputs (e.g. a saved report that carries both an `equals` and
  // an `isAnyOf` item for the same field) would otherwise accumulate dupes.
  quick[SHIPMENT_QUICK.statuses] = Array.from(new Set(statuses));
  quick[SHIPMENT_QUICK.shipmentTypes] = Array.from(new Set(shipmentTypes));

  return {
    ...EMPTY_FILTERS,
    quick,
    advancedFilters: advancedFiltersFromModel(rest, SHIPMENT_FILTER_FIELD_DEFINITIONS),
  };
};

/** Shipment types, shaped for the dropdown. Same code/name pair as statuses. */
export const buildShipmentTypeOptions = (
  data: Array<{ code: string; name: string }> | undefined
): LabelValue[] => {
  if (!data) return [];
  return data
    .map((t) => ({ label: t.name, value: t.code }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const buildStatusOptions = (
  data: Array<{ code: string; name: string }> | undefined
): LabelValue[] => {
  if (!data) return [];
  return data
    .map((s) => ({ label: s.name, value: s.code }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

// Re-exported so callers keep a single import for this list's configuration;
// the definitions live in `shipmentListColumns`.
export { buildColumnDefs, buildInitialColumnSelection } from './shipmentListColumns';
